// This composable is used for detecting when you are close to bottom of the current page in order to facilitate
// loading new data (all data loading done outside of this composable). This currently only works when scrolling
// the whole window, but should be usable with scrolling a particular element with minor tweaks.
import { onMounted, onUnmounted, nextTick } from 'vue';

// `fetchData` must be a function that returns Promise that calls `resolve` on success.
// `shouldFetchAtEndOfScroll` is an optional computed property that returns a bool to determine if
// `fetchData` should trigger. It defaults to true.
const useInfiniteScroll = ({ fetchData, shouldFetchAtEndOfScroll = { value: true } }) => {
  const isCloseToBottom = () => {
    const distanceToScrollBottom = document.body.offsetHeight - (window.innerHeight + window.scrollY);
    return distanceToScrollBottom < 200;
  };

  const fetchDataIfNeeded = (ignoreCloseToBottom = false) => {
    // nextTick to allow time for the data to be rendered, which affects the scrollHeight calculation.
    nextTick(() => {
      if (shouldFetchAtEndOfScroll.value && (ignoreCloseToBottom || isCloseToBottom())) {
        // Fetch data, then try to fetch data again. This means that after a fetch, if there's more data to be
        // loaded (shouldFetchAtEndOfScroll) but not enough was loaded to fill up the page (isCloseToBottom), more data
        // is loaded to try to fill up the page.
        fetchData().then(() => {
          fetchDataIfNeeded();
        });
      }
    });
  };

  // onScroll takes an event arg that we don't care about, so this calls fetchDataIfNeeded indirectly to drop it.
  const onScroll = () => {
    fetchDataIfNeeded();
  };

  const fetchFirstPage = () => {
    fetchDataIfNeeded(true);
  };

  // Lifecycle hooks
  onMounted(() => {
    window.addEventListener('scroll', onScroll);
    fetchFirstPage();
  });
  onUnmounted(() => {
    window.removeEventListener('scroll', onScroll);
  });

  return { fetchFirstPage };
};

export default useInfiniteScroll;
